/**
 * Client-side Zana answers when the payments server / Zana AI is unreachable.
 *
 * Takeover scripts stay server-resolved when online; this path never invents a
 * walkthrough. It answers greetings, live High Risk counts, cached briefing
 * lines, and simple "open X" chips from data already on the open dashboard.
 */

import { zanaServerSetupHint } from '../utils/paymentsBaseUrl';
import { asList } from '../utils/asList';
import { forbidsAlertClear as forbidsAlertClearCanonical } from './zanaPatterns';

export interface LocalAskBrief {
  headline?: string;
  summary?: string;
  source?: string;
  recommendations?: Array<{ text?: string }>;
}

export interface LocalAskContext {
  brief?: LocalAskBrief | null;
  unreadAlertCount?: number;
  highRiskBusCount?: number;
  busCount?: number;
  activeBusCount?: number;
  page?: string;
  entityType?: 'bus' | 'route' | 'driver';
  entityId?: string;
  cards?: { label: string; value: string }[];
  /** Set when we probed GET /health on the payments API. */
  paymentsReachable?: boolean;
  paymentsConfigured?: boolean;
  /** Result of a real Zana AI self-test (connection asks). */
  zanaAiProbed?: boolean;
  zanaAiOk?: boolean;
  zanaAiSample?: string;
  zanaAiError?: string;
}

/** Mirror of AskResult — kept local to avoid a circular import with briefingsApi. */
export interface LocalAskResult {
  ok: boolean;
  answer?: string;
  mascotState?: string;
  source?: 'ai' | 'deterministic' | 'cache' | 'static';
  message?: string;
  intents?: Array<{
    actionId: string;
    params?: {
      tab?: string;
      busId?: string;
      plate?: string;
      message?: string;
      entityType?: string;
      entityId?: string;
      scriptId?: string;
      title?: string;
      month?: string;
      format?: string;
      /* Ranged reports (export_report) and their schedules (schedule_report).
       * Mirrors ZanaIntent['params'] in services/zanaActions.ts — the two are
       * separate types on purpose (this one is the offline shape) but they must
       * agree on field names or an intent built here is silently unusable. */
      preset?: string;
      from?: string;
      to?: string;
      cadence?: string;
      hourUtc?: number;
      weekday?: number;
      dayOfMonth?: number;
      id?: string;
      note?: string;
    };
    confirmRequired?: boolean;
  }>;
  takeoverOffer?: {
    topic: string;
    kind: 'do' | 'show';
    label: string;
    explainLabel?: string;
    prompt: string;
  };
  aiStatus?: 'unavailable';
}

function qc(question: string): string {
  return String(question || '')
    .toLowerCase()
    .replace(/\bzana\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A system the connectivity words below could plausibly be ABOUT.
 *
 * The point of requiring one is that "connect", "online" and "reach" are
 * ordinary English before they are networking terms.
 */
const SYSTEM_SUBJECT = '(servers?|backend|api|engine|brain|dock|console|internet|network|wifi|ai|model|claude|you|u|yourself)';

/**
 * Requests for reasoning that happen to contain a connectivity word.
 *
 * "Connect the dots" is the single most valuable question an owner can ask, and
 * the old bare `\bconnect\b` rule below answered it by pinging a server. Same
 * class of miss: "reach out to the parents" is an op, not a health check.
 */
const CONNECT_FALSE_FRIENDS = [
  /\b(connect|connecting|join|joining|link|linking|piece|piecing|put)\b(\s+\w+){0,2}\s+(dots|pieces|threads|picture|together)\b/,
  /\breach\s+out\b/,
  /\b(connect|link)\b.{0,16}\b(with|to)\b.{0,16}\b(parents?|drivers?|school|guardians?)\b/,
];

/* ── Which month did they actually ask for? ───────────────────────────────
   Mirror of `resolveMonthKey` in the backend's premium/zanaActions.js. Keep the
   two in step: this shortcut runs BEFORE the fetch, so a client that cannot
   read "July" beats a server that can. */
const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

export function monthKeyNow(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** `YYYY-MM` for whatever period this sentence names, or null if it names none. */
export function resolveMonthKey(text: string, now: Date = new Date()): string | null {
  const q = String(text || '').toLowerCase();
  const iso = (q.match(/\b(20\d{2})-(0[1-9]|1[0-2])\b/) || [])[0];
  if (iso) return iso;
  if (/\blast month\b|\bprevious month\b/.test(q)) {
    return monthKeyNow(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)));
  }
  if (/\bthis month\b|\bcurrent month\b/.test(q)) return monthKeyNow(now);
  for (let i = 0; i < MONTH_NAMES.length; i += 1) {
    const name = MONTH_NAMES[i];
    const re = new RegExp(`\\b${name.slice(0, 3)}(${name.slice(3)})?\\b`, 'i');
    if (!re.test(q)) continue;
    const year = (q.match(/\b(20\d{2})\b/) || [])[1];
    if (year) return `${year}-${String(i + 1).padStart(2, '0')}`;
    const y = i > now.getUTCMonth() ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
    return `${y}-${String(i + 1).padStart(2, '0')}`;
  }
  return null;
}

/** "July 2026" from "2026-07", so the answer names the month it will download. */
export function monthLabel(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(String(key || ''));
  if (!m) return key;
  const idx = Number(m[2]) - 1;
  const name = MONTH_NAMES[idx];
  if (!name) return key;
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${m[1]}`;
}

/** Meta asks about dock vs payments API — answered with an optional /health probe. */
export function isConnectionMetaQuestion(question: string): boolean {
  const q = qc(question);
  if (!q) return false;
  // Vetoes run first, always. Every rule below is a keyword rule, and a keyword
  // rule that fires on a sentence it was never meant for is invisible: the
  // owner gets a confident, fluent, completely unrelated answer.
  if (CONNECT_FALSE_FRIENDS.some((re) => re.test(q))) return false;
  // Words that can only be about the plumbing.
  if (/\b(backend|servers?|api|endpoint|connectivity|self[- ]?test|health check)\b/.test(q)) {
    return true;
  }
  // Everything else needs a system as its subject. Proximity in either
  // direction — "are you connected", "is the connection to your engine ok".
  const generic = '(connect|connected|connecting|connection|disconnected|online|offline|reachable|unreachable|reach)';
  if (new RegExp(`\\b${generic}\\b[^?.!]{0,28}\\b${SYSTEM_SUBJECT}\\b`).test(q)
    || new RegExp(`\\b${SYSTEM_SUBJECT}\\b[^?.!]{0,28}\\b${generic}\\b`).test(q)) {
    return true;
  }
  if (/\b(zana ai|ai brain|your brain|deep ai|model|claude)\b/.test(q)) {
    return true;
  }
  /* `qc()` strips the word "zana" before any of this runs, so the `zana ai`
   * alternative above can never match — "is Zana AI live?" arrives here as
   * "is ai live" and fell through to the engine as an ordinary question.
   * Pre-existing, found while fixing bug 58. Matched narrowly: a bare `\bai\b`
   * would hijack real questions like "is the AI losing us money?", which is
   * the same mistake in the other direction. */
  if (/\bai\b[^?.!]{0,30}\b(live|online|up|working|connected|answering|reachable|awake|ok)\b/.test(q)) {
    return true;
  }
  /* "are you …" counts ONLY when the rest of the sentence is about being
   * reachable.
   *
   * This was a bare `\bare you\b`, and it swallowed "how are you today?" — so
   * the friendliest thing an owner can say to Zana was classified as a
   * connectivity probe and answered with "Trasse server is reachable." Two
   * things made that worse than a mismatched reply. The local shortcut runs
   * BEFORE the fetch, so chit-chat never reached the engine that would have
   * answered it properly; and a deterministic branch means asking twice
   * produces the identical non-answer twice, which is what made her read as a
   * machine that had not understood a word (bug 58). */
  if (/\b(are|r) (you|u)\b[^?.!]{0,40}?\b(connect(ed)?|online|offline|up|down|live|alive|reachable|responding|working|there|awake|broken|dead)\b/.test(q)) {
    return true;
  }
  if (/\b(is (the )?engine|engine work|working ok|you up)\b/.test(q)) {
    return true;
  }
  if (/\bhey\b.*\b(connect|online|server|ai)\b/.test(q)) {
    return true;
  }
  return false;
}

/**
 * Chit-chat: "how are you?", "how's it going?", "you good?".
 *
 * Answered here rather than passed to the engine on purpose. Every ask that
 * misses the engine's deterministic rules ships the org's whole aggregate to
 * Claude (~26k tokens, see `classify.js`), and "how are you" is not worth a
 * model call. Instant and free is also the better answer.
 */
export function isSmallTalk(question: string): boolean {
  const q = qc(question);
  if (!q) return false;
  if (isConnectionMetaQuestion(question)) return false;
  return /\b(how (are|r) (you|u)|how('?s| is) it going|how('?s| is) your (day|morning|afternoon|evening)|how (have|'?ve) you been|how you doing|(you|u) (doing )?(ok|okay|good|well|alright)|hope (you'?re|you are) (well|good)|what'?s up|nice to (meet|see) you)\b/.test(q);
}

/* Rotating copy.
 *
 * The same sentence word-for-word on a repeat is half of bug 58 — Gabriel
 * asked twice and got a byte-identical non-answer, which reads as nobody home.
 * A counter rather than `Math.random()` so a tester can reproduce the sequence
 * they saw. */
let replyTurn = 0;

function rotate(options: string[]): string {
  const pick = options[replyTurn % options.length];
  replyTurn += 1;
  return pick;
}

/**
 * Phrases that hand Zana the keys — a mirror of `TAKEOVER_RE` in the engine's
 * `zana/engine/classify.js`. Keep the two in step.
 *
 * It has to exist on the client as well as the server because the local
 * shortcut below runs BEFORE the fetch: "take over and show me my drivers"
 * matches the plain navigate rule on the word "show", so without this the most
 * explicit handover there is never reaches the gate that would have composed a
 * walkthrough, and the owner gets a tab-open chip instead. Missing a handover
 * this way is invisible — she answers, just not with the wheel.
 */
const TAKEOVER_RE = new RegExp([
  'take\\s+(the\\s+)?(wheel|reins|lead)',
  'take\\s+(over|control|charge)',
  '\\btake-?over\\b',
  '(you|zana)\\s+(can\\s+|could\\s+|should\\s+|please\\s+)?drive',
  'drive\\s+(the\\s+)?(dashboard|console|screen|session|it)\\b',
  'you\\s+(handle|deal with|sort|fix|solve|do|run)\\s+(it|this|that|the rest|them)',
  '(handle|deal with|sort|fix|solve)\\s+(it|this|that|them)\\s+for\\s+me',
  '(clear|triage|mark)\\s+(my\\s+|the\\s+|all\\s+)?(high[- ]?risk\\s+)?(alerts?|queue)\\b',
  // Mirrors the server's qualified clause: "clear the traffic detected/delays
  // alerts" is still a handover even with adjectives between "the" and
  // "alerts" (determiner mandatory so adjectival "clear" can't trigger it).
  '(clear|triage|mark)\\s+(my|the|all|these|those)\\s+(?:[\\w/-]+\\s+){1,4}?(alerts?|queue)\\b',
  'can you\\s+(clear|triage|mark)\\b.{0,40}\\b(alerts?|high[- ]?risk)\\b',
  // UI tour — not strategy ("walk me through how to cut fuel costs…").
  '(walk|show)\\s+me\\s+(through|around)(?!\\s+how\\b)',
  'give\\s+me\\s+(a|the)\\s+tour',
].join('|'), 'i');

/** Is the owner handing over the wheel? Such asks must reach the live engine. */
export function isTakeoverRequest(question: string): boolean {
  return TAKEOVER_RE.test(String(question || ''));
}

/** Step-by-step / how-to guides — must compose a walkthrough on the server.
 *
 * 2026-09-17: deliberately narrower than the engine's GUIDE_RE in
 * zana/engine/intent.js. This one runs BEFORE every free local shortcut, and
 * a bare "how do i" / "how to" sent "how do I send an SMS" to the server Ask
 * (possible model tokens) and left it no offline answer. Only phrasings that
 * ask to be walked through something skip the shortcuts; a plain "how do I…"
 * that no shortcut claims still reaches the server, where the engine's wider
 * pattern still applies. */
const GUIDE_RE = /\b(step[- ]by[- ]step|walk me through|teach me how|show me how|guide me)\b/i;

export function isGuideRequest(question: string): boolean {
  return GUIDE_RE.test(String(question || ''));
}

/**
 * Asks that want judgement — a mirror of `ADVICE_PATTERNS` + `SYNTHESIS_PATTERNS`
 * in the engine's `zana/engine/classify.js`. Keep the two in step.
 *
 * Same reason `TAKEOVER_RE` is duplicated here: the local shortcut runs BEFORE
 * the fetch, and several of these sentences carry a word the shortcut grabs.
 * "Show me the top 3 things you'd fix" matches the plain navigate rule on
 * "show" and was answered with a tab-open chip; "what's the biggest problem"
 * matched nothing and still never left the browser. A reasoning question that
 * never reaches the engine cannot be reasoned about, however good the engine is
 * — which is most of what the stress test measured.
 */
const REASONING_RE = new RegExp([
  '\\bwhy\\b',
  '\\b(what should (i|we)|what do (i|you) (do|think|recommend|suggest)|advise|advice|recommend)\\b',
  "\\b(what'?s causing|root cause|how come)\\b",
  '\\b(connect|connecting|join|link)\\b(\\s+\\w+){0,2}\\s+dots\\b',
  '\\b(top|first|best|main|key)\\s+(\\d+|one|two|three|four|five|few)\\b.{0,44}\\b(thing|things|fix|fixes|problem|problems|issue|issues|priorit\\w+|action|actions|change|changes|move|moves|win|wins|area|areas)\\b',
  '\\b(\\d+|two|three|four|five)\\s+(things|fixes|problems|issues|priorities|actions|changes|moves|wins)\\b',
  "\\b(what|anything)\\b.{0,24}\\b(am i missing|i'?m missing|should i know|are you seeing|do you see|stands out|jumps out|worries you|concerns you)\\b",
  '\\b(biggest|worst|main|number one)\\b.{0,26}\\b(problem|issue|risk|concern|worry|waste|leak|threat|mistake)\\b',
  "\\b(you'?d|you would|would you)\\s+(fix|change|do|prioriti[sz]e|start with|tackle|focus)\\b",
  '\\b(if you (were|ran)|in my (shoes|position)|your (take|view|opinion|read|call|verdict))\\b',
  '\\b(explain|diagnos\\w+|investigate|dig into|deep dive|make sense of|help me understand)\\b',
  '\\b(losing|wasting|leaking|bleeding)\\b.{0,24}\\b(money|fuel|costs?|spend|shillings?|ksh)\\b',
  '\\b(should (i|we)|is it worth|do you think)\\b',
  '\\b(compare|comparison|breakdown|break it down|trade-?off)\\b',
  '\\b(trend|pattern|over time|getting (better|worse))\\b',
  '\\b(prioriti[sz]e|focus on|where do i start|what now)\\b',
  '\\breason (it|this|them) out\\b',
  "\\b(what'?s|what is) (actually )?(on fire|burning|urgent)\\b",
  '\\bwhat can wait\\b',
  '\\bfix my morning\\b',
  "\\bdon'?t dump\\b",
  '\\btalk like a colleague\\b',
  "\\b(i'?m|i am) stressed\\b",
  '\\b(should i worry|anything .{0,16}worry)\\b',
  '\\b(which|what).{0,28}(actually )?matters\\b',
  '\\bparents? (are )?(calling|on my case|complaining)\\b',
  '\\bsasa niaje\\b',
  '\\btell me about\\b',
  '^(hi|hey|hello|yo|habari|sasa|niaje)\\b.+',
  '\\bhow are things\\b',
  '\\bin plain (english|words)\\b',
].join('|'), 'i');

/** Does this ask want thinking rather than a lookup? Then it must reach the engine. */
export function isReasoningRequest(question: string): boolean {
  return REASONING_RE.test(String(question || ''));
}

/* "Open the fuel report" is navigation. "Bring up the buses WITHOUT a driver"
 * is a query wearing a navigation verb, and answering it by opening a tab
 * throws the filter away — the owner asked which ones, and got a page.
 *
 * This matters more than it looks because the local bank runs BEFORE the
 * server is called at all: a question it claims never reaches the engine that
 * could have answered it. So the rule is to yield whenever a qualifier is
 * present, because the server can always do both — answer, and offer the chip
 * that opens the page anyway. */
const FILTERED_LOOKUP_RE = new RegExp([
  // superlatives — "the worst route", "my highest spend"
  '\\b(worst|best|slowest|fastest|highest|lowest|most|least|top|bottom)\\b',
  // absence and negation — "buses without a driver", "not refuelled"
  '\\b(without|missing|lacking|no longer|never|not)\\b',
  // thresholds and comparisons
  '\\b(over|under|above|below|more than|less than|older than|worse than)\\b',
  // explicit filtering language
  '\\b(only|except|that (have|are|need)|which (have|are|need)|with no)\\b',
].join('|'), 'i');

export function isFilteredLookup(question: string): boolean {
  return FILTERED_LOOKUP_RE.test(String(question || ''));
}

/** SMS / broadcast text to drivers — not a report schedule, and not a capability we have. */
export function isSmsCapabilityAsk(question: string): boolean {
  const q = qc(question);
  if (!q) return false;
  if (/\b(in[- ]app|in the app)\b/.test(q) && !/\bsms\b/.test(q)) return false;
  if (/\bsms\b/.test(q)) return true;
  return /\btext\b/.test(q) && /\b(all (my |the )?drivers?|every driver|the drivers?)\b/.test(q);
}

/** Operator / mood / triage asks that must never be answered as a High Risk count. */
export function isOperatorTriageAsk(question: string): boolean {
  const q = String(question || '');
  return /\bfix my morning\b/i.test(q)
    || /\b(sat down as|as the) (the )?operator\b/i.test(q)
    || /\bdon'?t dump\b/i.test(q)
    || /\btalk like a colleague\b/i.test(q)
    || /\bon fire\b/i.test(q)
    || /\bwhat can wait\b/i.test(q)
    || /\breason (it|this) out\b/i.test(q)
    || /\bdon'?t open\b.{0,24}\b(high[- ]?risk|alerts?)\b/i.test(q);
}

/* Was a second, DIVERGED copy of this regex — it had drifted a clause behind
   the one in zanaAskShared. Re-exported from the canonical module so both
   surfaces answer "don't clear my alerts" the same way. */
export { forbidsAlertClear } from './zanaPatterns';

/** Pure lookup of the card — "how many alerts?", "open high risk" — not triage. */
function isPureAlertLookup(question: string): boolean {
  const q = qc(question);
  if (!q) return false;
  if (isReasoningRequest(question) || isOperatorTriageAsk(question) || forbidsAlertClearCanonical(question)) {
    return false;
  }
  return /^(how many|any|are there|do i have)\b.{0,40}\b(high[- ]?risk|alerts?|unread)\b/.test(q)
    || /^(open|show|list|go to)\s+(the\s+)?(high[- ]?risk|alerts?)\b/.test(q)
    || /\bhow many\b.{0,24}\b(alerts?|high[- ]?risk|unread)\b/.test(q)
    || /\bany (unread )?alerts?\b/.test(q);
}

/* ── Reports and schedules ──────────────────────────────────────────────────
 * These are caught EARLY because each has a near-miss branch further down that
 * would otherwise swallow it: "email me a summary next Tuesday" matched the
 * word `summary` in the fleet-overview branch and came back as a briefing, and
 * a request for a weekly report came back as this month's CSV.
 *
 * Both used to be answered with an honest refusal. They are now real: the
 * backend builds any date range as a PDF and can mail one on a cadence, so the
 * job here is to PARSE the ask into the right intent rather than apologise for
 * it. What remains genuinely impossible — Excel, and a one-off send on a single
 * named future date — is still said plainly, because a near-miss delivered
 * confidently is worse than a refusal. */
/* A FUTURE TIME must be present. An earlier version of this listed bare
 * "remind me" as sufficient on its own, and it immediately refused "Remind me
 * roughly what excursions are sitting on the books at the moment" — a request
 * for information RIGHT NOW — with "I can't schedule things". Refusing a
 * question you can answer is its own kind of wrong answer, so every trigger
 * below either names a time or is unambiguously about the future. */
/* Tier A — phrases that ARE a scheduling request on their own, whatever else
 * the sentence says. "Let me know when the bus arrives" names no deliverable
 * and still cannot be done. */
const SCHEDULE_EXPLICIT_RE = new RegExp([
  '\\bset (up )?an? (reminder|alert|schedule)\\b',
  '\\bremind me (on|at|in|next|tomorrow|every|each|later|to|about it|when)\\b',
  '\\bschedule (a|an|the|this|it|me)\\b',
  '\\b(keep me posted|let me know when|alert me when|notify me when|ping me when|email me when|tell me when|message me when)\\b',
  '\\b(from now on|going forward|every time|each time)\\b',
  '\\bkeep (watching|tracking|monitoring|an eye on)\\b',
].join('|'), 'i');

/* Tier B — a future time. On its own that is just a date, so it only counts as
 * a scheduling ask when the sentence also names something to deliver. */
const SCHEDULE_WHEN_RE = new RegExp([
  '\\b(next|every|each|on|by|come) (mon|tues|wednes|thurs|fri|satur|sun)day\\b',
  '\\b(tomorrow|later today|later this week|next week|next month|in an hour|in \\d+ (minutes?|hours?|days?|weeks?))\\b',
  '\\b(every|each) (day|week|month|morning|evening|afternoon)\\b',
  '\\b(daily|weekly|monthly|nightly|recurring|repeating|automatic(ally)?) (report|email|e-mail|update|summary|digest|reminder|briefing)\\b',
].join('|'), 'i');
const SCHEDULE_WHAT_RE = /\b(e-?mails?|sends?|sending|reports?|updates?|summar(y|ies)|digests?|notif\w+|tells?|track\w*|watch\w*|monitor\w*|remind\w*|alerts?|pings?|brief\w*)\b/i;

/** "Email me a summary next Tuesday", "keep watching this driver", "every Friday…" */
export function isScheduleRequest(question: string): boolean {
  const s = String(question || '');
  if (isSmsCapabilityAsk(s)) return false;
  if (SCHEDULE_EXPLICIT_RE.test(s)) return true;
  return SCHEDULE_WHEN_RE.test(s) && SCHEDULE_WHAT_RE.test(s);
}

/** A cadence we can actually honour, or null when it is a one-off future date. */
export function parseCadence(question: string):
  { cadence: 'daily' | 'weekly' | 'monthly'; weekday?: number; dayOfMonth?: number } | null {
  const q = String(question || '').toLowerCase();
  const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  // "every Friday" / "each monday" — recurring. "next Friday" is a one-off and
  // deliberately does NOT match: we cannot send on a single named date.
  const weekly = /\b(every|each)\s+(sun|mon|tues|wednes|thurs|fri|satur)day\b/.exec(q);
  if (weekly) {
    const idx = DAYS.findIndex((d) => d.startsWith(weekly[2]));
    return { cadence: 'weekly', weekday: idx < 0 ? 1 : idx };
  }
  if (/\b(every|each)\s+(week|monday)\b|\bweekly\b/.test(q)) return { cadence: 'weekly', weekday: 1 };
  if (/\b(every|each)\s+(day|morning|evening)\b|\bdaily\b|\bnightly\b/.test(q)) return { cadence: 'daily' };
  if (/\b(every|each)\s+month\b|\bmonthly\b/.test(q)) {
    const dom = /\bday\s+(\d{1,2})\b|\b(\d{1,2})(st|nd|rd|th)\b/.exec(q);
    const n = dom ? Number(dom[1] || dom[2]) : 1;
    return { cadence: 'monthly', dayOfMonth: n >= 1 && n <= 28 ? n : 1 };
  }
  return null;
}

const ISO_DATE_RE = /\b(\d{4}-\d{2}-\d{2})\b/g;

/** Which period the owner means, in the shape `export_report` wants. */
export function parseReportPeriod(question: string):
  { preset?: string; from?: string; to?: string } | null {
  const q = String(question || '').toLowerCase();
  const dates = String(question || '').match(ISO_DATE_RE) || [];
  if (dates.length >= 2) return { from: dates[0], to: dates[1] };
  if (dates.length === 1) return { from: dates[0], to: dates[0] };
  if (/\blast week\b|\bpast week\b|\bprevious week\b/.test(q)) return { preset: 'last week' };
  if (/\bthis week\b|\bweek so far\b/.test(q)) return { preset: 'this week' };
  if (/\byesterday\b/.test(q)) return { preset: 'yesterday' };
  if (/\btoday\b/.test(q)) return { preset: 'today' };
  if (/\blast month\b|\bprevious month\b/.test(q)) return { preset: 'last month' };
  if (/\bthis month\b/.test(q)) return { preset: 'this month' };
  if (/\bweekly\b/.test(q)) return { preset: 'last week' };
  if (/\bdaily\b/.test(q)) return { preset: 'yesterday' };
  if (/\bmonthly\b/.test(q)) return { preset: 'last month' };
  return null;
}

/** CSV only when explicitly asked for — a "report" means a document. */
export function parseReportFormat(question: string): 'pdf' | 'csv' {
  return /\b(csv|spreadsheet|raw rows|excel)\b/i.test(String(question || '')) ? 'csv' : 'pdf';
}

/* Still genuinely impossible. Both PDF and every date range came OFF this list
 * when the report engine landed — only the spreadsheet formats remain
 * unimplemented. Leaving "weekly" here was the bug that sent a perfectly
 * buildable weekly report into the Excel refusal. */
const NON_CSV_FORMAT_RE = /\b(excel|xlsx|google sheet|google docs)\b/i;

/** Wants a FILE covering a period — the trigger for `export_report`. */
export function isReportRequest(question: string): boolean {
  const s = String(question || '');
  if (!/\b(report|export|download|csv|pdf|statement)\b/i.test(s)) return false;
  // "the last 8 monthly reports" is a batch ask with its own honest answer
  // further down (we can do one month at a time). Don't intercept it.
  if (/\bmonthly reports?\b/i.test(s) && /\b(\d+|all|every|several|each)\b/i.test(s)) return false;
  // "why is my fuel report zero" is a question about a screen, not a download.
  if (/\b(why|what|how come|explain)\b/i.test(s) && !/\b(export|download|send me|give me|pull)\b/i.test(s)) {
    return false;
  }
  return parseReportPeriod(s) != null;
}

/** Export asks for a format we still cannot produce at all. */
export function isUnsupportedExportRequest(question: string): boolean {
  return NON_CSV_FORMAT_RE.test(String(question || ''));
}

const TEST_CONNECTION_CHIP: LocalAskResult['intents'] = [{ actionId: 'test_zana_connection' }];

function withConnectionTest(body: LocalAskResult): LocalAskResult {
  return { ...body, intents: TEST_CONNECTION_CHIP };
}

/** True for asks the dock should answer instantly without waiting on the API. */
export function canAnswerLocally(question: string): boolean {
  const q = qc(question);
  if (!q) return true;
  // First, before every shortcut below: handing over the wheel outranks them
  // all. `localAskZana` still answers this offline (an honest nav chip and
  // "walkthroughs need the live server"), but only once the fetch has actually
  // failed — never instead of trying.
  if (isTakeoverRequest(question)) return false;
  if (isGuideRequest(question)) return false;
  // Second, and for the same reason: a question that wants judgement must reach
  // the engine even when it also contains a word one of the shortcuts below
  // matches. There is no local answer to "why are we losing money" that is
  // worth having, so a shortcut that produces one is strictly a loss.
  if (isReasoningRequest(question) || isOperatorTriageAsk(question)) return false;
  if (isSmsCapabilityAsk(question)) return true;
  if (/\bwhat can you not\b|\bwhat can'?t you\b|\bwhat are you (not|unable)\b/.test(qc(question))
    || (/\bwhat can you\b/.test(qc(question)) && /\bnot do\b/.test(qc(question)))) {
    return true;
  }
  // Third: things we cannot do at all. Answer these here rather than letting a
  // near-miss branch below turn "a weekly report" into this month's CSV, or
  // "email me a summary next Tuesday" into a briefing. A mixed ask that also
  // wants judgement was already sent to the engine by the check above, which is
  // right — the engine's own rules refuse the unsupported half.
  if (isUnsupportedExportRequest(question) || isScheduleRequest(question) || isReportRequest(question)) return true;
  // Writes / ops that need confirm chips — do not swallow as a High Risk count.
  if (/\b(close|close out|resolve)\b.{0,35}\b(incidents?|reports?)\b/.test(q)
    || /\b(contact|message|notify|whatsapp|sms|tell)\b.{0,60}\bparents?\b/.test(q)
    || /\b(delete|create|add|replace)\b.{0,40}\b(bus|vehicle)\b/.test(q)
    || /\b(email|text|sms)\b.{0,45}\b(driver|coach|d\d+)\b/.test(q)
    || /\bmonthly reports?\b/.test(q)) {
    return true; // answered locally with the real confirmable chip when possible
  }
  if (/^(hi+|hey+|hello+|yo+|hiya|heya|hola|howdy|sup|wa?ssup|good (morning|afternoon|evening|day)|greetings)[\s!.,'’?]*$/.test(q)) {
    return true;
  }
  if (/^(thanks|thank you|thx|ty|cheers|ok(ay)?|got it)[\s!.,'’]*$/.test(q)) return true;
  if (isSmallTalk(question)) return true;
  if (/what can you|what do you do|who are you|^help\b|what should i ask/.test(q)) return true;
  if (/\b(what am i looking at|what'?s on (my |the )?screen|where am i)\b/.test(q)) return true;
  if (isPureAlertLookup(question)) return true;
  if (/\bfleet size\b/.test(q)
    || /\b(how many|number of)\b.{0,24}\b(bus|buses|vehicles?)\b/.test(q)
    || /\b(bus|buses|vehicles?)\b.{0,24}\b(do (we|i) have|are active)\b/.test(q)) {
    return true;
  }
  if (/how('?s| is) (the |my )?fleet\b/.test(q)
    || /\b(fleet overview|fleet summary|fleet briefing|fleet status)\b/.test(q)) return true;
  if (/\b(open|show|take me|go to|bring|jump to)\b/.test(q) && !forbidsAlertClearCanonical(question)
    && !isReasoningRequest(question) && !isOperatorTriageAsk(question)
    && !isFilteredLookup(question)) return true;
  if (isConnectionMetaQuestion(question)) return true;
  return false;
}

/**
 * Build a local ask result, or null when this question needs the live engine.
 */
export function localAskZana(question: string, ctx: LocalAskContext = {}): LocalAskResult | null {
  const qRaw = String(question || '').trim();
  const q = qc(qRaw);
  const unread = Math.max(0, Number(ctx.unreadAlertCount) || 0);
  const highRisk = Math.max(0, Number(ctx.highRiskBusCount) || 0);
  const busCount = Math.max(0, Number(ctx.busCount) || 0);
  const activeBusCount = Math.max(0, Number(ctx.activeBusCount) || 0);
  const brief = ctx.brief;

  /* Chit-chat first, and deliberately ahead of the connection branch: this is
   * the ordering bug 58 turned on. Answer the question that was actually
   * asked, then say something useful about the fleet — a status line alone is
   * not a reply to "how are you". */
  if (isSmallTalk(qRaw)) {
    const busy = highRisk > 0 || unread > 0;
    const unreadPart = unread > 0
      ? ` and ${unread} unread alert${unread === 1 ? '' : 's'}`
      : '';
    const opener = busy
      ? rotate([
        `I'm fine — it's the fleet that wants attention: High Risk is showing ${highRisk}${unreadPart}.`,
        `Good, thanks. Your buses are the busy ones today — High Risk is at ${highRisk}${unreadPart}.`,
        `No complaints on my side. High Risk is showing ${highRisk}${unreadPart} whenever you want to look.`,
      ])
      : rotate([
        'Good — and so is the fleet. Nothing unread, High Risk clear.',
        "I'm well, thanks for asking. Quiet screen: nothing unread, High Risk at 0.",
        'All fine here, and nothing has needed you since you last looked.',
      ]);
    return {
      ok: true,
      answer: `${opener} What do you want to look at?`,
      mascotState: busy ? 'alert' : 'happy',
      source: 'static',
      intents: busy ? [{ actionId: 'open_high_risk_alerts' }] : undefined,
    };
  }

  if (isConnectionMetaQuestion(qRaw)) {
    const configured = ctx.paymentsConfigured !== false;
    const serverUp = ctx.paymentsReachable === true;
    const serverProbed = typeof ctx.paymentsReachable === 'boolean';
    const aiProbed = ctx.zanaAiProbed === true;
    const aiOk = ctx.zanaAiOk === true;
    const risk = highRisk > 0 ? ` I still see High Risk at ${highRisk} from this dashboard.` : '';
    const retest = ' Tap Test Zana AI below to run it again.';

    if (!configured) {
      return withConnectionTest({
        ok: true,
        answer:
          "You're asking if my Zana AI brain is live — I need the Trasse server configured (REACT_APP_PAYMENTS_URL) before I can test that."
          + risk
          + ' I can still greet you and read High Risk on this screen.',
        mascotState: 'concerned',
        source: 'static',
        aiStatus: 'unavailable',
      });
    }
    if (aiProbed && aiOk && ctx.zanaAiSample) {
      const sample = ctx.zanaAiSample.length > 100
        ? `${ctx.zanaAiSample.slice(0, 100)}…`
        : ctx.zanaAiSample;
      return withConnectionTest({
        ok: true,
        answer: `Yes — Zana AI is connected and answering. I just heard back: “${sample}”.${risk}${retest}`,
        mascotState: 'happy',
        source: 'static',
      });
    }
    if (aiProbed && !aiOk && serverUp) {
      const why = ctx.zanaAiError ? ` (${ctx.zanaAiError})` : '';
      return withConnectionTest({
        ok: true,
        answer:
          `Trasse server is up, but Zana AI is not live right now — I'm on facts, counts, and navigation only${why}.`
          + risk
          + retest,
        mascotState: 'concerned',
        source: 'static',
        aiStatus: 'unavailable',
      });
    }
    if (serverProbed && serverUp) {
      return withConnectionTest({
        ok: true,
        answer:
          'Trasse server is reachable.'
          + risk
          + ' Tap Test Zana AI below — that is the real check that my brain is answering (not just the API).',
        mascotState: 'happy',
        source: 'static',
      });
    }
    if (serverProbed && !serverUp) {
      return withConnectionTest({
        ok: true,
        answer:
          "I can't test Zana AI until the Trasse server is reachable — that's where the AI call runs."
          + risk
          + ` ${zanaServerSetupHint()}`
          + " Tap Test Zana AI when you're ready.",
        mascotState: 'concerned',
        source: 'static',
        aiStatus: 'unavailable',
      });
    }
    return withConnectionTest({
      ok: true,
      answer:
        "Sounds like you're asking if my Zana AI brain is online — not just this dock."
        + risk
        + " Tap Test Zana AI below and I'll ping the server and make one real AI call.",
      mascotState: 'talking',
      source: 'static',
    });
  }

  if (!q || /^(hi+|hey+|hello+|yo+|hiya|heya|hola|howdy|sup|wa?ssup|good (morning|afternoon|evening|day)|greetings)[\s!.,'’?]*$/.test(q)) {
    const risk = highRisk > 0
      ? ` High Risk is showing ${highRisk} right now`
        + (unread > 0 ? ` (${unread} unread alert${unread === 1 ? '' : 's'}).` : '.')
      : ' Fleet looks quiet on the High Risk card.';
    return {
      ok: true,
      answer: rotate([
        `Hey! 👋 I'm Zana — watching this fleet from your console.${risk} Ask me about alerts, fuel, or say "open high risk".`,
        `Hello — I'm here and watching.${risk} Ask me about alerts or fuel, or tell me to open a screen.`,
        `Hi 👋${risk} Anything you want checked, ask — alerts, fuel, routes, or say "open high risk".`,
      ]),
      mascotState: 'happy',
      source: 'static',
    };
  }

  if (/^(thanks|thank you|thx|ty|cheers|ok(ay)?|got it)[\s!.,'’]*$/.test(q)) {
    return {
      ok: true,
      answer: "Anytime — I'm in the corner keeping watch.",
      mascotState: 'happy',
      source: 'static',
    };
  }

  const cannotAsk = /\bwhat can you not\b|\bwhat can'?t you\b|\bwhat are you (not|unable)\b/.test(q)
    || (/\bwhat can you\b/.test(q) && /\bnot do\b/.test(q));
  if (cannotAsk) {
    return {
      ok: true,
      answer:
        'Blunt list. I cannot SMS — transactional SMS is not set up. I cannot in-app chat a named driver. '
        + 'I cannot call anyone or look up personal phones and home addresses. I cannot fire or suspend from quoted speech — only from you, after confirm. '
        + 'I cannot settle where a child ended up. I cannot silently write. '
        + 'I can email a driver after you confirm, send one in-app parent update on a named plate after confirm, walk you through a job you named (no timer), and read this fleet.',
      mascotState: 'talking',
      source: 'static',
    };
  }

  if (/what can you|what do you do|who are you|^help\b|what should i ask|what can i ask/.test(q)) {
    return {
      ok: true,
      answer:
        'I read this org\'s data and can open the right screen. Try: "how many alerts?", "how\'s the fleet?", "open high risk", or "show fuel". Deeper advice needs the live server.',
      mascotState: 'talking',
      source: 'static',
      intents: [
        { actionId: 'open_high_risk_alerts' },
        { actionId: 'open_fuel_report' },
      ],
    };
  }

  if (/\b(what am i looking at|what'?s on (my |the )?screen|where am i|which (tab|page|view))\b/.test(q)) {
    const page = String(ctx.page || 'dashboard');
    const cards = Array.isArray(ctx.cards) ? ctx.cards.slice(0, 6) : [];
    const cardBits = cards
      .map((c) => `${c.label} ${c.value}`)
      .filter((s) => s.trim().length > 1);
    const focus = ctx.entityType === 'bus' && ctx.entityId
      ? ` Focused vehicle: ${ctx.entityId}.`
      : '';
    return {
      ok: true,
      answer: cardBits.length
        ? `You're on ${page}.${focus} The cards in front of you read: ${cardBits.join(' · ')}.`
        : `You're on ${page}.${focus} Ask about a card or plate on this screen and I'll pull the numbers.`,
      mascotState: 'talking',
      source: 'static',
    };
  }

  /* Unsupported capabilities, ahead of every branch that would otherwise take a
   * near-miss swing at them. Both of these shipped as confident wrong answers:
   * a weekly report came back as this month's CSV, and "email me a summary next
   * Tuesday" came back as a fleet briefing. Say no, then offer the real thing. */
  if (isSmsCapabilityAsk(qRaw)) {
    return {
      ok: true,
      answer:
        'I cannot SMS. Transactional SMS is not set up on this organisation. '
        + 'I can email a named driver after you confirm, or send one in-app parent update on a named plate. '
        + 'Nothing has been sent.',
      mascotState: 'talking',
      source: 'static',
    };
  }

  /* Excel and Sheets are still not built. Say so, then offer what exists rather
     than leaving them with nothing. */
  if (isUnsupportedExportRequest(qRaw)) {
    const period = parseReportPeriod(qRaw) || { preset: 'last month' };
    return {
      ok: true,
      answer:
        "I can't do Excel or Google Sheets — I produce PDF or CSV. A CSV opens straight "
        + 'in Excel if that\'s what you need it for. Say the word and I\'ll pull it for the '
        + 'period you asked about.',
      mascotState: 'thinking',
      source: 'static',
      intents: [
        { actionId: 'export_report', params: { ...period, format: 'csv' }, confirmRequired: true },
      ],
    };
  }

  /* A report for any period — this is now a real capability, so parse it rather
     than apologise. PDF unless they asked for CSV. */
  const reportPeriod = isReportRequest(qRaw) ? parseReportPeriod(qRaw) : null;
  if (reportPeriod) {
    const format = parseReportFormat(qRaw);
    const label = reportPeriod.from && reportPeriod.to
      ? (reportPeriod.from === reportPeriod.to
        ? reportPeriod.from
        : `${reportPeriod.from} to ${reportPeriod.to}`)
      : String(reportPeriod.preset);
    const cadence = parseCadence(qRaw);
    const intents: LocalAskResult['intents'] = [
      { actionId: 'export_report', params: { ...reportPeriod, format }, confirmRequired: true },
    ];
    // "send me last week's and keep doing it" is two asks; offer both chips.
    if (cadence) {
      intents.push({
        actionId: 'schedule_report',
        params: { ...cadence, hourUtc: 6, format },
        confirmRequired: true,
      });
    }
    return {
      ok: true,
      answer:
        `I can pull ${label} as a ${format.toUpperCase()} — per-vehicle runs, distance, fuel, `
        + 'alerts and attendance, with the totals and any data caveats on the last page. '
        + (cadence ? 'I can also set it to arrive automatically. ' : '')
        + 'Confirm below and nothing happens until you do.',
      mascotState: 'talking',
      source: 'static',
      intents,
    };
  }

  if (isScheduleRequest(qRaw)) {
    const cadence = parseCadence(qRaw);
    const format = parseReportFormat(qRaw);
    if (cadence) {
      return {
        ok: true,
        answer:
          `I can do that — a ${cadence.cadence} ${format.toUpperCase()} emailed to your own `
          + 'address, covering the period that just closed. It goes to the address on your '
          + 'admin account; I can\'t send it to anyone else. Confirm and I\'ll set it running.',
        mascotState: 'talking',
        source: 'static',
        intents: [
          { actionId: 'schedule_report', params: { ...cadence, hourUtc: 6, format }, confirmRequired: true },
          { actionId: 'list_report_schedules' },
        ],
      };
    }
    // A cadence we could not read means a one-off future date, which is the one
    // piece of scheduling that genuinely does not exist.
    return {
      ok: true,
      answer:
        "I can't send something on one specific future date — I can only do a repeating "
        + 'schedule: every day, every week, or every month. I can also just pull it for you '
        + 'now. Which would you rather?',
      mascotState: 'thinking',
      source: 'static',
      intents: [
        { actionId: 'export_report', params: { preset: 'last week', format: 'pdf' }, confirmRequired: true },
      ],
    };
  }

  if (/\b(close|close out|resolve)\b.{0,35}\b(incidents?|reports?)\b/.test(q)
    || /\b(incidents?|reports?)\b.{0,35}\b(resolved|closed|done)\b/.test(q)) {
    return {
      ok: true,
      answer:
        'Confirm below and I\'ll close out open incident reports so those buses can leave High Risk — history stays on each bus page. Nothing changes until you confirm.',
      mascotState: 'talking',
      source: 'static',
      intents: [
        { actionId: 'resolve_incidents', confirmRequired: true },
        { actionId: 'open_high_risk_alerts' },
      ],
    };
  }

  if (/\b(wrong stop|dropped at the wrong|kid was dropped|child was dropped|not the (right|correct) stop)\b/.test(q)
    || (/\bparents?\b/.test(q) && /\b(wrong stop|drop[- ]?off)\b/.test(q))) {
    const plateMatch = qRaw.toUpperCase().match(/\b[A-Z]{2,3}\d{2,4}[A-Z]?\b/);
    const plate = plateMatch ? plateMatch[0] : (ctx.entityType === 'bus' ? ctx.entityId : '');
    const intents: LocalAskResult['intents'] = [
      { actionId: 'list_parent_dropoff_flags' },
      { actionId: 'open_tab', params: { tab: 'liveMap' } },
      { actionId: 'open_high_risk_alerts' },
    ];
    if (plate) {
      intents.unshift({
        actionId: 'contact_late_bus_parents',
        confirmRequired: true,
        params: {
          plate,
          message: 'We are checking a drop-off report on this run. Please stay with your child; we will update you in the app.',
        },
      });
    }
    return {
      ok: true,
      answer:
        'Right now: 1) Live Map — is that bus still on the corridor. 2) High Risk — any parent-flagged drop-off. 3) Call the driver on that run. '
        + 'I can list the drop-offs parents flagged (read). I will not settle where a child is from chat. '
        + (plate
          ? `I can send one in-app update to authorized parents on ${plate} after you confirm. I cannot SMS.`
          : 'Name the plate and I can send one confirmable in-app parent update. I cannot SMS.'),
      mascotState: 'alert',
      source: 'static',
      intents,
    };
  }

  if (/\b(contact|message|notify|whatsapp|sms|tell)\b.{0,60}\bparents?\b/.test(q)
    || /\bparents?\b.{0,40}\b(late|running late|contact|message)\b/.test(q)) {
    const plateMatch = qRaw.toUpperCase().match(/\b[A-Z]{2,3}\d{2,4}[A-Z]?\b/);
    const plate = plateMatch ? plateMatch[0] : (ctx.entityType === 'bus' ? ctx.entityId : '');
    if (plate) {
      return {
        ok: true,
        answer: `I can send one neutral app update to the authorized parents on ${plate} after you confirm. Nothing has been sent yet.`,
        mascotState: 'talking',
        source: 'static',
        intents: [
          {
            actionId: 'contact_late_bus_parents',
            confirmRequired: true,
            params: {
              plate,
              message: 'Your bus is running late. Please check the app for live updates.',
            },
          },
          { actionId: 'open_bus', params: { busId: plate } },
        ],
      };
    }
    return {
      ok: true,
      answer: 'Name the bus plate (or open that bus) and I\'ll offer a confirmable parent update. Live Map shows who is on the road.',
      mascotState: 'talking',
      source: 'static',
      intents: [{ actionId: 'open_tab', params: { tab: 'liveMap' } }],
    };
  }

  if (/\b(delete|remove|deactivate|create|add|replace)\b.{0,40}\b(bus|vehicle)\b/.test(q)) {
    return {
      ok: true,
      answer:
        "I won't create, replace, or delete a bus from chat. Use Fleet and its own confirmation screen — those changes affect drivers, parents, and trip history. Nothing has been changed.",
      mascotState: 'talking',
      source: 'static',
      intents: [{ actionId: 'open_tab', params: { tab: 'fleet' } }],
    };
  }

  if (/\bmonthly reports?\b/.test(q)
    && /\b(generate|create|export|download|csv|email|send|eight|all|every|\d+)\b/.test(q)) {
    // Named month wins; today's month only when the sentence names none. This
    // shortcut had NO month parsing at all and unconditionally used the current
    // month — and because it runs before the fetch, it beat the server's
    // parser too. Asking for July and being handed August started here.
    const asked = resolveMonthKey(qRaw);
    const month = asked || monthKeyNow();
    return {
      ok: true,
      answer:
        `I can download the ${monthLabel(month)} CSV after you confirm`
        + (asked ? '.' : " — that's the current month; say a month name if you meant another.")
        + ' I cannot batch several months or email the files from chat — open Monthly Report for the rest. Nothing has been emailed.',
      mascotState: 'talking',
      source: 'static',
      intents: [
        // A download moves fleet data out of the tenant, so it is confirm-gated
        // even though it is classified read-only for the server's risk model.
        // Without this flag the answer said "Confirm below" and no confirm
        // control ever rendered — `intentNeedsConfirm` derives purely from the
        // write list, so the promise was structurally a lie.
        { actionId: 'export_monthly_report', params: { month, format: 'csv' }, confirmRequired: true },
        { actionId: 'open_tab', params: { tab: 'monthlyReport' } },
      ],
    };
  }

  if (isOperatorTriageAsk(qRaw)) {
    const fire = highRisk > 0
      ? `On fire: High Risk is ${highRisk}${unread > 0 ? ` with ${unread} unread` : ''} — that is what parents feel. After lunch: silent buses and fuel logging, unless a child-whereabouts row is in that queue.`
      : unread > 0
        ? `${unread} unread in the queue, High Risk card clear — triage the unread first.`
        : 'Nothing on the High Risk card looks like a fire from this screen.';
    return {
      ok: true,
      answer:
        (/\b(sasa|niaje|colleague)\b/.test(q) ? 'Sawa — ' : (/\bstress/.test(q) ? 'Got it. Short version: ' : ''))
        + fire
        + ' I will not dump the card. Open High Risk only if you want the list; otherwise tell me which plate or "show me missing refuels".',
      mascotState: highRisk > 0 ? 'alert' : 'talking',
      source: 'static',
      intents: highRisk > 0 ? [{ actionId: 'open_high_risk_alerts' }] : [{ actionId: 'open_tab', params: { tab: 'liveMap' } }],
    };
  }

  if (/\b(email|text|sms)\b.{0,45}\b(driver|coach|d\d+)\b/.test(q)
    || (/\bemail\b/.test(q) && /\b[a-z]{2,3}\d{2,4}[a-z]\b/.test(q))) {
    if (isSmsCapabilityAsk(qRaw) && !/\bemail\b/.test(q)) {
      return {
        ok: true,
        answer:
          'I cannot SMS. Transactional SMS is not set up. Name the plate and I can email that driver after you confirm. Nothing has been sent.',
        mascotState: 'talking',
        source: 'static',
      };
    }
    const plateMatch = qRaw.toUpperCase().match(/\b[A-Z]{2,3}\s?\d{2,4}\s?[A-Z]\b/);
    const plate = plateMatch ? plateMatch[0].replace(/\s/g, '') : (ctx.entityType === 'bus' ? ctx.entityId : '');
    if (plate) {
      return {
        ok: true,
        answer: `I can email the authorized driver on ${plate} after you confirm. I never invent an address. Nothing has been sent.`,
        mascotState: 'talking',
        source: 'static',
        intents: [
          {
            actionId: 'email_driver',
            confirmRequired: true,
            params: { busId: plate, note: 'Please log today\'s refill in Trasse (litres + odometer photo).' },
          },
        ],
      };
    }
    return {
      ok: true,
      answer: 'Name the plate (or a D-label) and I will arm an email to that driver after you confirm. I cannot SMS or in-app text them. Nothing has been sent.',
      mascotState: 'talking',
      source: 'static',
    };
  }

  if (isPureAlertLookup(qRaw)) {
    const answer = highRisk > 0
      ? `Live from your open dashboard: High Risk shows ${highRisk}`
        + (unread > 0
          ? ` — ${unread} unread alert${unread === 1 ? '' : 's'} in the queue.`
          : ' (incident clusters can light the card even with 0 unread).')
        + ' Tap below to open High Risk Alerts.'
      : unread > 0
        ? `You have ${unread} unread alert${unread === 1 ? '' : 's'}, but High Risk is at 0 right now.`
        : 'No unread alerts and High Risk is clear on this screen right now.';
    return {
      ok: true,
      answer,
      mascotState: highRisk > 0 || unread > 0 ? 'alert' : 'happy',
      source: 'static',
      intents: [{ actionId: 'open_high_risk_alerts' }],
      takeoverOffer: highRisk > 0
        ? {
          topic: 'alerts',
          kind: unread > 0 ? 'do' : 'show',
          label: unread > 0
            ? `Walk High Risk with me — clear my ${unread} alert${unread === 1 ? '' : 's'}`
            : 'Walk me through High Risk',
          explainLabel: 'Explain it in chat',
          prompt: 'Stay in chat, or hand me the screen when the server is back.',
        }
        : undefined,
    };
  }

  if (/\bfleet size\b/.test(q)
    || /\b(how many|number of)\b.{0,24}\b(bus|buses|vehicles?)\b/.test(q)
    || /\b(bus|buses|vehicles?)\b.{0,24}\b(do (we|i) have|are active)\b/.test(q)) {
    if (!busCount) {
      return {
        ok: true,
        answer: 'The open dashboard currently shows no buses on this organization.',
        mascotState: 'talking',
        source: 'static',
        intents: [{ actionId: 'open_tab', params: { tab: 'fleet' } }],
      };
    }
    const parked = Math.max(0, busCount - activeBusCount);
    /* Plain answer first, caveat only when it earns its place.
     *
     * This used to end every count with "that's this screen (this campus /
     * HQ), not necessarily every vehicle on the organisation" — a hedge
     * against a multi-site org reading one site's card as the whole fleet.
     * Real, but "campus / HQ" is language nobody running two school buses
     * uses, and it arrived attached to the simplest question Zana answers,
     * so a two-word fact came back sounding like a disclaimer. A school
     * with one site is told the number and nothing else; the caveat is kept
     * for the case that actually needs it. */
    const oneSite = busCount === activeBusCount || busCount <= 3;
    return {
      ok: true,
      answer: activeBusCount && activeBusCount !== busCount
        ? `You have ${busCount} buses: ${activeBusCount} active and ${parked} parked or out of service.${oneSite ? '' : " That's what this dashboard covers — another site's vehicles would not be counted here."}`
        : `You have ${busCount} buses${activeBusCount ? `, all ${activeBusCount} active` : ''}.`,
      mascotState: 'talking',
      source: 'static',
      intents: [{ actionId: 'open_tab', params: { tab: 'fleet' } }],
    };
  }

  if (/how('?s| is) (the |my )?fleet\b/.test(q)
    || /\b(fleet overview|fleet summary|fleet briefing|fleet status)\b/.test(q)) {
    if (brief?.headline || brief?.summary) {
      const tips = asList<{ text?: string }>(brief.recommendations)
        .map((r) => r.text)
        .filter(Boolean)
        .slice(0, 2)
        .map((t) => `• ${t}`)
        .join('\n');
      return {
        ok: true,
        answer:
          `${brief.headline || 'Fleet snapshot'}\n${brief.summary || ''}`
          + (tips ? `\n${tips}` : '')
          + (highRisk > 0 ? `\n\nLive: High Risk ${highRisk}.` : ''),
        mascotState: 'talking',
        source: 'cache',
      };
    }
    return {
      ok: true,
      // Don't promise the server is coming back — the usual cause is a config
      // problem that does not clear on its own, and an owner who waits and
      // retries just gets this same line again.
      answer: highRisk > 0
        ? `I couldn't pull a fresh briefing just now, so I can't give you the full overview. What I can see live on your screen: High Risk is ${highRisk}. Try once more, and if it stays like this it's our side to fix — tell support.`
        : "I couldn't pull a fresh briefing just now, so I can't give you the full overview. Try once more, and if it stays like this it's our side to fix — tell support.",
      mascotState: 'concerned',
      source: 'static',
      aiStatus: 'unavailable',
    };
  }

  // Simple navigation — chips only; takeover scripts still need the server.
  // Guide asks ("show me how to invite…") must not collapse to Analytics.
  if (isGuideRequest(qRaw)) return null;
  if (/\b(open|show|take me|go to|bring|jump to)\b/.test(q)) {
    if (/high[- ]?risk|alert/.test(q)) {
      return {
        ok: true,
        answer: 'Sure — tap below and I\'ll open High Risk Alerts. (Full walkthroughs need the live server.)',
        mascotState: 'talking',
        source: 'static',
        intents: [{ actionId: 'open_high_risk_alerts' }],
      };
    }
    if (/fuel\s*price/.test(q)) {
      return {
        ok: true,
        answer: 'Sure — tap below for Fuel prices.',
        mascotState: 'talking',
        source: 'static',
        intents: [{ actionId: 'open_tab', params: { tab: 'fuelPrices' } }],
      };
    }
    if (/\bfuel\b/.test(q)) {
      return {
        ok: true,
        answer: 'Sure — tap below for the fuel report.',
        mascotState: 'talking',
        source: 'static',
        intents: [{ actionId: 'open_fuel_report' }],
      };
    }
    let tab: string | null = null;
    if (/live\s*map|map\b/.test(q)) tab = 'liveMap';
    else if (/driver/.test(q)) tab = 'analytics';
    else if (/route/.test(q)) tab = 'analytics';
    else if (/fleet|bus(es)?\b/.test(q)) tab = 'fleet';
    else if (/dashboard|home\b/.test(q)) tab = 'dashboard';
    if (tab) {
      return {
        ok: true,
        answer: 'Sure — tap the chip below and I\'ll take you there. (Walkthroughs need the live server.)',
        mascotState: 'talking',
        source: 'static',
        intents: [{ actionId: 'open_tab', params: { tab } }],
      };
    }
  }

  return null;
}

/** Owner-facing copy when neither local nor server can help. */
export function serversUnreachableMessage(): string {
  return "I couldn't finish that request — the Trasse server may be waking up or your browser blocked the call. I can still answer fleet and High Risk counts already on this screen, check the connection, or open a tab.";
}
